;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { type ClassValue, clsx } from "clsx";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { every, isArray, isNil, isObject, isPlainObject } from "lodash-es";
import { twMerge } from "tailwind-merge";
import { ROUTES } from "@/lib/constants/routes";
import { logger } from "@/lib/services/logger";
import { isDataShieldEnv } from "@/lib/utils/is-data-shield-env";
import type { SourceConfig } from "@/types/visualization";
import type { WorkflowCondition } from "@/types/workflow";
import type { Workspace } from "@/types/workspace";

dayjs.extend(relativeTime);

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function capitalizeFirstLetter(string: string): string {
	if (!string) return string;
	return string.charAt(0).toUpperCase() + string.slice(1)?.toLowerCase();
}

export function capitalizeAllWords(string: string): string {
	if (!string) return string;
	return string
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1)?.toLowerCase())
		.join(" ");
}

export function toTitleCase(str: string): string {
	if (!str) return "";

	let processed = str;

	// Replace non-alphanumeric (except space) with space, then trim
	processed = processed.replace(/[^a-zA-Z0-9 ]/g, " ").trim();

	return processed
		.split(/(?<=[a-z])(?=[A-Z])|[\s\-_]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}

export function getUserRoleText(role: string): string {
	const roleMap: Record<string, string> = {
		ADMIN: "Admin",
		USER: "Member",
	};
	return roleMap[role] || role;
}

export function getMostRecentByDate<T extends Record<string, any>>(
	array: T[],
	dateKey: "startDate" | "endDate",
): T | null {
	if (!array.length) return null;

	return array.reduce((latest, item) =>
		dayjs(item[dateKey]).isAfter(dayjs(latest[dateKey])) ? item : latest,
	);
}

export function checkConditions(
	data: Record<string, any>,
	conditions: WorkflowCondition[],
): boolean {
	return conditions.every(({ field, value, operator }) => {
		const dataValue = data[field];
		switch (operator) {
			case ">":
				return dataValue > value;
			case "<":
				return dataValue < value;
			case ">=":
				return dataValue >= value;
			case "<=":
				return dataValue <= value;
			case "==":
				// biome-ignore lint/suspicious/noDoubleEquals: intentional loose equality for filter comparison
				return dataValue == value;
			case "!=":
				// biome-ignore lint/suspicious/noDoubleEquals: intentional loose equality for filter comparison
				return dataValue != value;
			default:
				return false;
		}
	});
}

/**
 * Get the default view path for a list of workspaces
 * @param workspaces - The list of workspaces
 * @param workspaceId - Optional workspace ID to get specific workspace path
 * @returns The default view path
 */
export function getWorkspacesDefaultViewPath(
	workspaces: Workspace[],
	workspaceId?: string,
) {
	if (!workspaces.length) {
		return "/select-workspace";
	}

	// If a specific workspace ID is provided, find that workspace
	if (workspaceId) {
		const targetWorkspace = workspaces.find(
			(workspace) => workspace.id === workspaceId,
		);
		if (targetWorkspace) {
			return `/${targetWorkspace.id}${ROUTES.LEADERBOARD}`;
		}
	}

	// Otherwise, use the first available workspace
	const firstWorkspace = workspaces[0];
	if (firstWorkspace) {
		return `/${firstWorkspace.id}${ROUTES.LEADERBOARD}`;
	}

	return "/select-workspace";
}

export const generateRequestDates = (
	sourceConfig: SourceConfig,
): {
	startDate: string;
	endDate: string;
}[] => {
	const {
		dateRange,
		interval,
		dateFormat = "YYYY-MM",
		isEndOfDate = false,
		dateDirection = "past",
	} = sourceConfig as SourceConfig;

	// Some data sources don't have a date range (or don't require date-scoped requests).
	// In that case, return a single empty window so callers that loop over requestDates
	// still execute once without throwing.
	if (!dateRange || typeof dateRange !== "string") {
		return [{ endDate: "", startDate: "" }];
	}

	// Use regex to get the number of months
	const dateRangeAmount = Number(dateRange.match(/(\d+)_MONTHS/i)?.[1]);
	const now = dayjs();

	if (interval === "MONTHLY") {
		const dates: { startDate: string; endDate: string }[] = [];

		// Generate dates based on direction
		if (dateDirection === "both") {
			// Past (includes current month like the 'past' branch)
			for (let i = 0; i < dateRangeAmount; i++) {
				const date = now.subtract(i, "month");
				if (isEndOfDate) {
					if (i === 0) {
						const isLastDay = date.isSame(date.endOf("month"), "day");
						if (!isLastDay) {
							dates.push({
								endDate: now.format(dateFormat),
								startDate: date.startOf("month").format(dateFormat),
							});
							continue;
						}
					}
					dates.push({
						endDate: date.endOf("month").format(dateFormat),
						startDate: date.startOf("month").format(dateFormat),
					});
				} else {
					dates.push({
						endDate: date.format(dateFormat),
						startDate: date.format(dateFormat),
					});
				}
			}
			// Future (starts next month like the 'future' branch)
			for (let i = 0; i < dateRangeAmount; i++) {
				const date = now.add(i + 1, "month");
				if (isEndOfDate) {
					dates.push({
						endDate: date.endOf("month").format(dateFormat),
						startDate: date.startOf("month").format(dateFormat),
					});
				} else {
					dates.push({
						endDate: date.format(dateFormat),
						startDate: date.format(dateFormat),
					});
				}
			}
		} else {
			for (let i = 0; i < dateRangeAmount; i++) {
				const date =
					dateDirection === "past"
						? now.subtract(i, "month")
						: now.add(i + 1, "month"); // +1 to start from next month for future

				if (isEndOfDate) {
					if (dateDirection === "past" && i === 0) {
						// For current month in past direction, handle partial month
						const isLastDay = date.isSame(date.endOf("month"), "day");
						if (!isLastDay) {
							dates.push({
								endDate: now.format(dateFormat),
								startDate: date.startOf("month").format(dateFormat),
							});
							continue;
						}
					}

					dates.push({
						endDate: date.endOf("month").format(dateFormat),
						startDate: date.startOf("month").format(dateFormat),
					});
				} else {
					dates.push({
						endDate: date.format(dateFormat),
						startDate: date.format(dateFormat),
					});
				}
			}
		}

		return dates;
	}

	if (dateRangeAmount) {
		if (dateDirection === "past") {
			// Return a single date range from today minus dateRangeAmount months to today
			const startDate = now.subtract(dateRangeAmount, "month");
			return [
				{
					endDate: now.format(dateFormat),
					startDate: startDate.format(dateFormat),
				},
			];
		}

		if (dateDirection === "future") {
			// Future direction
			const endDate = now.add(dateRangeAmount, "month");
			return [
				{
					endDate: endDate.format(dateFormat),
					startDate: now.format(dateFormat),
				},
			];
		}

		if (dateDirection === "both") {
			// Span both directions: startDate is X months in the past, endDate is X months in the future
			const startDate = now.subtract(dateRangeAmount, "month");
			const endDate = now.add(dateRangeAmount, "month");
			return [
				{
					endDate: endDate.format(dateFormat),
					startDate: startDate.format(dateFormat),
				},
			];
		}

		// Default fall-through behaves like 'past'
		const startDate = now.subtract(dateRangeAmount, "month");
		return [
			{
				endDate: now.format(dateFormat),
				startDate: startDate.format(dateFormat),
			},
		];
	}

	// Default fallback - current month
	return [
		{
			endDate: now.endOf("month").format(dateFormat),
			startDate: now.startOf("month").format(dateFormat),
		},
	];
};

export function formatRelativeTime(
	date: Date | string | null | undefined,
): string {
	if (!date) return "-";
	return dayjs(date).fromNow();
}

/**
 * Takes a text and returns the initials.
 * If the text has only one part, it returns the first character of that part.
 * If the text has more than one part, it returns the first character of the first two parts.
 *
 * @param {string} text - The text.
 * @returns {string} The initials of the text.
 */
export function getInitials(text: string): string {
	if (!text) return "";
	const parts = text.split(" ");
	if (parts.length === 1) return parts[0][0];
	return parts[0][0] + parts[1][0];
}

/**
 * Format property name for display (truncate to first name + initial)
 * Ignores special characters when extracting the second initial
 * @param {string} value - The property name to format
 * @returns {string} Formatted property name (e.g., "Property A" from "Property (Asia)")
 * @example
 * formatPropertyName("Property (Asia)") // "Property A"
 * formatPropertyName("Property Name") // "Property N"
 * formatPropertyName("Property") // "Property"
 */
export function formatPropertyName(value: string): string {
	const tokens = String(value)
		.split(/\s+/)
		.map((t) => t.trim())
		.filter((t) => t.length > 0);

	const first = tokens[0] ?? "";

	// Find the first alphanumeric character from the second token (ignoring special chars)
	let secondInitial = "";
	if (tokens[1]) {
		const cleanToken = tokens[1].replace(/[^A-Za-z0-9]/g, "");
		secondInitial = cleanToken[0] ?? "";
	}

	return secondInitial ? `${first} ${secondInitial}` : first;
}

/**
 * Generates a dashboard URL for a workflow visualization
 * @param workspaceId - The workspace ID
 * @param viewName - The view name
 * @param viewVisualizationId - The view visualization ID
 * @returns The complete dashboard URL
 */
export function generateDashboardUrl(
	workspaceId: string,
	viewName: string,
	viewVisualizationId?: string,
): string {
	const baseUrl = process.env.NEXT_PUBLIC_APP_URL || "";
	const normalizedViewName = viewName.toLowerCase();

	if (viewVisualizationId) {
		return `${baseUrl}/${workspaceId}/${normalizedViewName}#${viewVisualizationId}`;
	}

	return `${baseUrl}/${workspaceId}/${normalizedViewName}`;
}

export const normalizeSignedZero = (value: number) => {
	return Object.is(value, -0) ? 0 : value;
};

export const formatCurrency = (
	value: number,
	options?: Intl.NumberFormatOptions,
) => {
	const normalizedValue = normalizeSignedZero(value);
	return new Intl.NumberFormat("en-US", {
		currency: "USD",
		currencyDisplay: "symbol",
		maximumFractionDigits: 2,
		minimumFractionDigits: 0,
		notation: "standard",
		style: "currency",
		...options,
	}).format(normalizedValue);
};

export const formatPercentage = (
	value: number,
	options?: Intl.NumberFormatOptions,
) => {
	return new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 2,
		minimumFractionDigits: 0,
		notation: "standard",
		style: "percent",
		...options,
	}).format(value / (value ? 100 : 1));
};

/**
 * Recursively checks if a value is empty, handling various data types.
 *
 * @param value - The value to check
 * @returns true if the value is empty (deeply), false otherwise
 *
 * @example
 * isDeepEmpty(null) // true
 * isDeepEmpty('') // true
 * isDeepEmpty(0) // true
 * isDeepEmpty([]) // true
 * isDeepEmpty({}) // true
 * isDeepEmpty([[], {}, '', 0]) // true
 * isDeepEmpty([1, 2]) // false
 * isDeepEmpty({ a: 1 }) // false
 * isDeepEmpty({ a: '', b: [], c: 0 }) // true
 */
export const isDeepEmpty = (value: any): boolean => {
	// Handle null/undefined
	if (isNil(value)) return true;

	// Handle empty strings
	if (typeof value === "string") return value.length === 0;

	// Handle numbers - consider 0 as empty
	if (typeof value === "number") return value === 0;

	// Handle booleans - they are never empty
	if (typeof value === "boolean") return false;

	// Handle arrays - check if empty or all elements are deeply empty
	if (isArray(value)) {
		return value.length === 0 || every(value, (item) => isDeepEmpty(item));
	}

	// Handle Map/Set - check size
	if (value instanceof Map || value instanceof Set) {
		return value.size === 0;
	}

	// Handle plain objects - check if empty or all values are deeply empty
	if (isPlainObject(value)) {
		const keys = Object.keys(value);
		return keys.length === 0 || every(keys, (key) => isDeepEmpty(value[key]));
	}

	// Handle other objects (Date, RegExp, etc.) - they are never empty
	if (isObject(value)) return false;

	// Default: consider non-empty
	return false;
};

// Known keys where property names appear
const propertyKeys = new Set([
	"groupName",
	"property_name",
	"propertyName",
	"property",
	"propertyTitle",
	"communityName",
	"siteName",
	"buildingName",
	"portfolioPropertyName",
	"locationName",
	"name",
	"portfolio",
]);

// Known keys where staff names appear
const staffKeys = new Set([
	"userName",
	"assigned_user",
	"assignedUser",
	"managerName",
	"serviceTechName",
	"staffName",
	"agentName",
	"accounts:admin_set_name",
	"name",
]);

const workOrderSensitiveKeys = new Set([
	"instructions",
	"job_description",
	"jobDescription",
	"primary_tenant_phone_number",
	"primaryTenantPhoneNumber",
	"vendor",
	"vendorName",
	"work_order_issue",
	"workOrderIssue",
]);

export const maskString = (input: unknown, shouldMask = false): unknown => {
	if (!isDataShieldEnv()) {
		return input;
	}

	if (!shouldMask) {
		return input;
	}

	if (typeof input !== "string") return input;

	const trimmed = input.trim();
	if (trimmed.length === 0) return trimmed;

	// Stop words to ignore when generating masked initials
	const STOP_WORDS = new Set([
		"and",
		"of",
		"the",
		"at",
		"in",
		"on",
		"for",
		"with",
		"by",
		"or",
		"but",
		"nor",
		"yet",
		"so",
		"a",
		"an",
		"as",
		"to",
		"all",
	]);

	const words = trimmed.split(/\s+/).filter(Boolean);
	const maskedTokens: string[] = [];
	let significantIndex = 0;

	for (const rawWord of words) {
		const clean = rawWord.replace(/[^A-Za-z]/g, "");
		if (!clean) continue;
		if (STOP_WORDS.has(clean.toLowerCase())) continue;

		let visible = "";
		if (significantIndex === 0) {
			visible = toTitleCase(clean.slice(0, Math.min(2, clean.length)));
		} else {
			visible = toTitleCase(clean.slice(0, 1));
		}

		const asterisk = rawWord.length > 2 ? "***" : "*".repeat(rawWord.length);
		maskedTokens.push(`${visible}${asterisk}`);
		significantIndex += 1;
	}

	if (maskedTokens.length > 0) {
		const masked = maskedTokens.join(" ");
		return masked;
	}

	// Fallback: if no significant tokens were found, mask using first two characters
	const visible = toTitleCase(trimmed.slice(0, Math.min(2, trimmed.length)));
	return `${visible}***`;
};

// Recursively traverse and mask values by key context
const traverse = (value: any, parentKey?: string): any => {
	if (Array.isArray(value)) {
		return value.map((item) => traverse(item, parentKey));
	}

	if (value && typeof value === "object") {
		const result: Record<string, any> = Array.isArray(value) ? [] : {};
		for (const [key, val] of Object.entries(value)) {
			const lowerKey = key.toLowerCase();

			// Decide if this key should be masked
			let shouldMaskThisKey = false;

			if (propertyKeys.has(key)) {
				shouldMaskThisKey = true;
			}

			if (staffKeys.has(key)) {
				shouldMaskThisKey = true;
			}

			if (workOrderSensitiveKeys.has(key)) {
				shouldMaskThisKey = true;
			}

			// Also mask generic "name" only in property/staff contexts
			if (
				!shouldMaskThisKey &&
				["name", "groupname", "portfolio"].includes(lowerKey)
			) {
				const parentIsPropertyContext = [
					"property",
					"properties",
					"propertydata",
					"propertylist",
					"portfolio",
					"propertieswithstaff",
					"propertyGroups",
				].includes((parentKey || "").toLowerCase());
				const parentIsStaffContext = [
					"staff",
					"staffdata",
					"assignedstaff",
					"team",
					"personnel",
				].includes((parentKey || "").toLowerCase());

				if (parentIsPropertyContext || parentIsStaffContext) {
					shouldMaskThisKey = true;
				}
			}

			// If value is a primitive string and needs masking, mask directly
			if (
				shouldMaskThisKey &&
				(typeof val === "string" || Array.isArray(val))
			) {
				if (Array.isArray(val)) {
					result[key] = val.map((v) =>
						typeof v === "string" ? maskString(v, true) : traverse(v, key),
					);
				} else {
					result[key] = maskString(val, true);
				}
				continue;
			}

			// Otherwise keep traversing
			result[key] = traverse(val, key);
		}
		return result;
	}

	return value;
};

export const anonymizeData = (
	data: any,
	visualizationName: string,
	shouldAnonymize = false,
) => {
	if (!isDataShieldEnv()) {
		return data;
	}

	if (!shouldAnonymize) {
		return data;
	}

	if (!data || typeof data !== "object") return data;

	const vis = (visualizationName || "").toLowerCase();
	// Determine which fields to anonymize based on visualization name
	const shouldMaskProperty =
		vis.includes("properties") ||
		vis.includes("application") ||
		vis.includes("work order") ||
		vis.includes("rent ready") ||
		vis.includes("property activities") ||
		vis.includes("unit performance") ||
		vis.includes("lease expirations") ||
		vis.includes("delinquency") ||
		vis.includes("budget performance") ||
		vis.includes("ledger performance") ||
		vis.includes("income statement") ||
		vis.includes("showings") ||
		vis.includes("touch points") ||
		vis.includes("prospects") ||
		vis.includes("apps usage report") ||
		vis.includes("user activity report") ||
		vis.includes("reputation analysis") ||
		vis.includes("vendors") ||
		vis.includes("sources rating heatmap");

	const shouldMaskStaff =
		vis.includes("work order") ||
		vis.includes("property activities") ||
		vis.includes("showings") ||
		vis.includes("unit performance") ||
		vis.includes("lease expirations") ||
		vis.includes("touch points") ||
		vis.includes("prospects") ||
		vis.includes("apps usage report") ||
		vis.includes("user activity report") ||
		vis.includes("reputation analysis") ||
		vis.includes("sources rating heatmap") ||
		vis.includes("applications") ||
		vis.includes("vendors") ||
		vis.includes("property staff") ||
		vis.includes("application");

	if (!shouldMaskProperty && !shouldMaskStaff) {
		return data;
	}

	try {
		return traverse(data);
	} catch (error) {
		logger.error(
			"Error anonymizing data",
			error instanceof Error ? error : new Error(String(error)),
			{ feature: "anonymize-data" },
		);
		return data;
	}
};
